/* eslint-disable no-await-in-loop */
import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getNewOperationDate } from 'utils/getNewOperationDate';
import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';

import { setModal } from 'modules/_shared/redux/modalActions';
import { addOperation } from 'modules/transactions/redux/operationActions';
import { useGetLastOperationDate } from 'modules/transactions/hooks/useGetLastOperationDate';
import { fetchExistingPartnersForConstitution } from 'modules/_shared/components/Modals/Constitution/utils/fetchExistingPartnersForConstitution';

import { hasShowDetails } from 'utils/showDetails';
import { SharesTransferExcelModalContextType } from '../types/SharesTransferExcelModalContextType';
import { sharesTransferExcelModalInitValue } from '../constants/sharesTransferExcelModalInitValue';
import sharesTransferExcelModalSteps, {
  SharesTransferExcelModalStep,
} from '../constants/sharesTransferExcelModalSteps';
import {
  SharesTransferExcelPreview,
  SharesTransferLedgerState,
} from '../types/SharesTransferExcel';
import { parseSharesTransferExcel } from '../utils/parseSharesTransferExcel';
import { buildSharesTransferExcelPreview } from '../utils/buildSharesTransferExcelPreview';
import { fetchInitialLedgerForExcel } from '../utils/fetchInitialLedgerForExcel';
import { downloadSharesTransferExcelTemplate } from '../utils/downloadSharesTransferExcelTemplate';
import {
  mapSharesTransferOperationToPayload,
  resolveSharesTransferPartnersFromExcel,
} from '../utils/mapSharesTransferOperationToPayload';

const SharesTransferExcelModalContext =
  createContext<SharesTransferExcelModalContextType>(
    sharesTransferExcelModalInitValue
  );

export const SharesTransferExcelModalProvider: FC<{
  children: ReactNode;
  size?: string;
}> = ({ children }) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const user = useSelector((state: State) => state.user);
  const holdingClasses = useSelector((state: State) => state.holdingClasses);

  const [step, setStep] = useState<SharesTransferExcelModalStep>(
    sharesTransferExcelModalSteps.UPLOAD
  );
  const [excelFile, setExcelFile] = useState<File | undefined>();
  const [excelPreview, setExcelPreview] =
    useState<SharesTransferExcelPreview | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isParsingExcel, setIsParsingExcel] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [executeProgress, setExecuteProgress] = useState({
    completed: 0,
    total: 0,
  });

  const rawRowsRef = useRef<Record<string, unknown>[]>([]);
  const initialLedgerRef = useRef<SharesTransferLedgerState>({});
  const existingPartnersRef = useRef<{ _id: string; cif: string }[]>([]);
  const selectedTypesRef = useRef<Record<string, string>>({});

  const { lastOperationDate } = useGetLastOperationDate(
    actualSociety?._id
  ).data;

  const rebuildPreview = (selectedTypes: Record<string, string> = {}) => {
    const preview = buildSharesTransferExcelPreview({
      rawRows: rawRowsRef.current,
      holdingClasses: holdingClasses || [],
      existingPartners: existingPartnersRef.current,
      initialLedger: initialLedgerRef.current,
      selectedTypesByOperationId: selectedTypes,
    });

    setExcelPreview(preview);
    return preview;
  };

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleDownloadExcelTemplate = () => {
    downloadSharesTransferExcelTemplate();
  };

  const handleBackToUpload = () => {
    setStep(sharesTransferExcelModalSteps.UPLOAD);
  };

  const handleChangeExcelFile = async (selectedFile: File) => {
    if (!selectedFile || !actualSociety?._id) return;

    setExcelFile(selectedFile);
    setIsParsingExcel(true);
    selectedTypesRef.current = {};

    try {
      const [parseResult, fetchedExistingPartners] = await Promise.all([
        parseSharesTransferExcel(selectedFile),
        fetchExistingPartnersForConstitution(actualSociety._id),
      ]);

      if (!parseResult.success) {
        errorSwal(t('SharesTransferExcelError_SHEET_NOT_FOUND'));
        setExcelFile(undefined);
        return;
      }

      rawRowsRef.current = parseResult.rows;
      existingPartnersRef.current = fetchedExistingPartners;

      const holdingClassPreview = buildSharesTransferExcelPreview({
        rawRows: parseResult.rows,
        holdingClasses: holdingClasses || [],
        existingPartners: fetchedExistingPartners,
        initialLedger: {},
      });

      if (!holdingClassPreview.holdingClassId) {
        setExcelPreview(holdingClassPreview);
        setStep(sharesTransferExcelModalSteps.PREVIEW);
        return;
      }

      const initialLedger = await fetchInitialLedgerForExcel({
        societyId: actualSociety._id,
        holdingClassId: holdingClassPreview.holdingClassId,
        existingPartners: fetchedExistingPartners,
      });

      initialLedgerRef.current = initialLedger;

      const preview = rebuildPreview();

      setStep(sharesTransferExcelModalSteps.PREVIEW);
      setExcelPreview(preview);
    } catch {
      errorSwal(t('SharesTransferExcelParseError'));
      setExcelFile(undefined);
    } finally {
      setIsParsingExcel(false);
    }
  };

  const handleSelectOperationType = (
    operationId: string,
    operationType: string
  ) => {
    selectedTypesRef.current = {
      ...selectedTypesRef.current,
      [operationId]: operationType,
    };

    const preview = rebuildPreview(selectedTypesRef.current);
    setExcelPreview(preview);
  };

  const handleExecuteFromExcel = async () => {
    if (!excelPreview || !actualSociety?._id || !excelPreview.holdingClassId) {
      return;
    }

    if (excelPreview.totalErrors > 0) {
      errorSwal(t('SharesTransferExcelCorrectErrorsToContinue'));
      return;
    }

    const validOperations = excelPreview.operations.filter(
      (operation) => operation.isValid
    );

    try {
      setIsExecuting(true);
      setIsLoading(true);
      setExecuteProgress({ completed: 0, total: validOperations.length });

      const allRows = validOperations.flatMap((operation) => operation.rows);
      const firstDate = validOperations[0]?.date || new Date();
      const partnerIdByCif = await resolveSharesTransferPartnersFromExcel({
        rows: allRows,
        society: actualSociety,
        user,
        operationDate: firstDate,
        existingPartners: existingPartnersRef.current,
      });

      let currentLastOperationDate: Date | string = lastOperationDate;

      for (let index = 0; index < validOperations.length; index += 1) {
        const operation = validOperations[index];

        if (hasShowDetails()) {
          const operationType =
            operation.selectedOperationType ||
            operation.operationTypes[0] ||
            '';

          // eslint-disable-next-line no-console
          console.log('[SharesTransferExcel] Ejecutando operación', {
            index: index + 1,
            total: validOperations.length,
            operationId: operation.operationId,
            date: operation.date,
            operationType,
            sellers: operation.sellers.length,
            buyers: operation.buyers.length,
            movements: operation.rows.length,
            isValid: operation.isValid,
          });
        }

        const payloadData = await mapSharesTransferOperationToPayload({
          operation,
          partnerIdByCif,
          holdingClassId: excelPreview.holdingClassId,
          society: actualSociety,
        });

        const newOperationDate = getNewOperationDate(
          currentLastOperationDate,
          payloadData.date
        );

        await store.dispatch(
          addOperation({
            movements: payloadData.movements,
            transactions: payloadData.transactions,
            date: newOperationDate,
            nominalValue: actualSociety.nominalValue,
            society: actualSociety._id,
            user: user._id,
            documents: [],
            comments: payloadData.comments,
            notarialRegistration: {
              notaryName: '',
              notaryCity: '',
              protocolNr: '',
            },
            operationType: payloadData.operationType,
            alias: '',
          })
        );

        currentLastOperationDate = newOperationDate;
        setExecuteProgress({
          completed: index + 1,
          total: validOperations.length,
        });
      }

      successSwal(
        t('SharesTransferExcelExecuteSuccess', {
          count: validOperations.length,
        })
      );
      handleCloseModal();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t('SharesTransferExcelExecuteError');

      errorSwal(message);
    } finally {
      setIsExecuting(false);
      setIsLoading(false);
    }
  };

  return (
    <SharesTransferExcelModalContext.Provider
      value={{
        data: {
          step,
          excelPreview,
          excelFile,
          isLoading,
          isParsingExcel,
          isExecuting,
          executeProgress,
          holdingClassId: excelPreview?.holdingClassId || null,
          holdingClassName: excelPreview?.holdingClassName || null,
        },
        actions: {
          handleCloseModal,
          handleChangeExcelFile,
          handleDownloadExcelTemplate,
          handleBackToUpload,
          handleSelectOperationType,
          handleExecuteFromExcel,
        },
      }}
    >
      {children}
    </SharesTransferExcelModalContext.Provider>
  );
};

export const useSharesTransferExcelModal = () =>
  useContext(SharesTransferExcelModalContext);
